import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  User, 
  CreditCard, 
  Settings, 
  Edit, 
  Heart, 
  Users, 
  ArrowLeft,
  Coins,
  Calendar,
  TrendingUp,
  Eye,
  LogOut,
  Crown
} from 'lucide-react';
import CreditDashboard from './CreditDashboard';
import BillingDashboard from './BillingDashboard';
import PersonalityProfile from './PersonalityProfile';
import ProfileManagement from './ProfileManagement';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`
      };

      // Load user profile
      const profileResponse = await axios.get(`${API}/me`, { headers });
      setProfile(profileResponse.data);

      // Load credit balance
      try {
        const creditResponse = await axios.get(`${API}/credits/balance`, { headers });
        setCreditBalance(creditResponse.data);
      } catch (error) {
        console.log('Credits not available');
      }

      // Load subscription status
      try {
        const subResponse = await axios.get(`${API}/subscription/status`, { headers });
        setSubscriptionStatus(subResponse.data);
        console.log('Subscription status loaded:', subResponse.data);
      } catch (error) {
        console.log('Subscription info not available:', error);
      }

    } catch (error) {
      console.error('Error loading account data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-4">Loading Account</h2>
              <p className="text-gray-300">Fetching your account information</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-x-hidden w-full max-w-full">
      {/* Sophisticated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-rose-400/20 via-transparent to-transparent"></div>
      
      <div className="relative z-10 min-h-screen px-4 py-8 w-full max-w-full overflow-x-hidden">
        <div className="max-w-6xl mx-auto w-full">
          {/* Header */}
          <div className="space-y-4 mb-8">
            {/* Top row - Back button and action buttons */}
            <div className="flex justify-between items-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 text-xs sm:text-sm px-2 sm:px-4"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
              
              <div className="flex space-x-2">
                <Button
                  onClick={() => navigate('/profile/setup')}
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm px-2 sm:px-4"
                >
                  <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">Edit</span>
                </Button>
                
                <Button
                  onClick={() => {
                    logout(navigate);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm px-2 sm:px-4"
                  title="Logout"
                >
                  <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">Logout</span>
                </Button>
              </div>
            </div>
            
            {/* Title row */}
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-light text-white">Account Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-300">Manage your dating experience</p>
            </div>
          </div>

          {/* Tab Navigation - Responsive horizontal scroll with proper sizing */}
          <div className="w-full max-w-full overflow-x-hidden">
            <div className="flex space-x-2 sm:space-x-4 mb-8 overflow-x-auto pb-2 scrollbar-thin w-full">
            {[
              { id: 'overview', label: 'Overview', mobileLabel: 'Overview', icon: User },
              { id: 'profile', label: 'Profile', mobileLabel: 'Profile', icon: Eye },
              { id: 'credits', label: 'Credits', mobileLabel: 'Credits', icon: Coins },
              { id: 'billing', label: 'Billing', mobileLabel: 'Billing', icon: CreditCard },
              { id: 'settings', label: 'Settings', mobileLabel: 'Settings', icon: Settings }
            ].map(({ id, label, mobileLabel, icon: Icon }) => (
              <Button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl transition-all whitespace-nowrap text-xs sm:text-sm font-medium ${
                  activeTab === id
                    ? 'bg-rose-600 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                <Icon className="hidden sm:flex h-4 w-4 flex-shrink-0 mr-2" />
                <span className="block sm:hidden">{mobileLabel}</span>
                <span className="hidden sm:block">{label}</span>
              </Button>
            ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid lg:grid-cols-3 gap-8 w-full max-w-full">
              {/* Profile Summary */}
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <User className="h-5 w-5 mr-2 text-rose-400" />
                    Profile Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    {profile?.photo_url ? (
                      <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white/20 shadow-xl mx-auto mb-4">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}`}
                          alt={profile?.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold" style={{display: 'none'}}>
                          {profile?.name?.charAt(0) || 'U'}
                        </div>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold ring-4 ring-white/20 shadow-xl">
                        {profile?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <h3 className="text-white font-bold text-lg">{profile?.name}</h3>
                    <p className="text-gray-300 text-sm">{profile?.age} years old</p>
                    <Badge className="mt-2 bg-green-500/20 text-green-300 border-green-400/30">
                      Profile Active
                    </Badge>
                  </div>
                  
                  <div className="mt-6 space-y-3">
                    <Button
                      onClick={() => navigate('/matches')}
                      className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Browse Matches
                    </Button>
                    <Button
                      onClick={() => navigate('/profile/setup')}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Credit Summary */}
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Coins className="h-5 w-5 mr-2 text-yellow-400" />
                    Credits & Balance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">
                      {creditBalance?.total_balance || '0'}
                    </div>
                    <p className="text-gray-300 text-sm mb-4">Available Credits</p>
                    
                    {creditBalance?.breakdown && Object.keys(creditBalance.breakdown).length > 0 && (
                      <div className="space-y-2 text-sm">
                        {Object.entries(creditBalance.breakdown).map(([type, data]) => (
                          <div key={type} className="flex justify-between text-gray-300">
                            <span>{type.replace('_', ' ').charAt(0).toUpperCase() + type.replace('_', ' ').slice(1)}:</span>
                            <span className="text-white">${data.amount?.toFixed(2) || '0.00'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => setActiveTab('credits')}
                    className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700 text-white"
                  >
                    Manage Credits
                  </Button>
                </CardContent>
              </Card>

              {/* Subscription Summary */}
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-purple-400" />
                    Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <Badge className={`text-lg px-4 py-2 mb-4 ${
                      subscriptionStatus?.subscription?.tier === 'vip' 
                        ? 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
                        : subscriptionStatus?.subscription?.tier === 'premium'
                        ? 'bg-purple-500/20 text-purple-300 border-purple-400/30'
                        : 'bg-gray-500/20 text-gray-300 border-gray-400/30'
                    }`}>
                      {subscriptionStatus?.subscription?.tier === 'vip' ? 'VIP Member' 
                        : subscriptionStatus?.subscription?.tier === 'premium' ? 'Premium Member' 
                        : 'Free Member'}
                    </Badge>
                    
                    {(subscriptionStatus?.subscription?.tier === 'premium' || subscriptionStatus?.subscription?.tier === 'vip') ? (
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-300">Next billing:</p>
                        <p className="text-white font-medium">{subscriptionStatus.next_billing_date || 'N/A'}</p>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-300">Upgrade for unlimited features</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {(subscriptionStatus?.subscription?.tier === 'premium' || subscriptionStatus?.subscription?.tier === 'vip') ? (
                      <Button
                        onClick={() => navigate('/billing/subscription')}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Manage Subscription
                      </Button>
                    ) : (
                      <Button
                        onClick={() => navigate('/subscription/plans')}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                      >
                        Upgrade Now
                      </Button>
                    )}
                    
                    {subscriptionStatus?.subscription?.tier === 'vip' && (
                      <Button
                        onClick={() => navigate('/coaching/dashboard')}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white w-full"
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        Access VIP Features
                      </Button>
                    )}
                    
                    {subscriptionStatus?.subscription?.tier === 'premium' && (
                      <Button
                        onClick={() => navigate('/premium/dashboard')}
                        className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        Access Premium Features
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="max-w-6xl mx-auto">
              <ProfileManagement />
            </div>
          )}

          {/* Credits Tab */}
          {activeTab === 'credits' && (
            <div>
              <CreditDashboard />
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div>
              <BillingDashboard />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
              <CardHeader>
                <CardTitle className="text-white">Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-white font-medium mb-4">Privacy Settings</h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Show me in search results</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Allow direct messages</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Show online status</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-white font-medium mb-4">Notification Settings</h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Email notifications</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Push notifications</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                      <label className="flex items-center justify-between">
                        <span className="text-gray-300">Match notifications</span>
                        <input type="checkbox" className="toggle-switch" defaultChecked />
                      </label>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <Button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and will delete all your data including matches, messages, and profile.')) {
                          try {
                            console.log('Account deletion requested');
                            const token = localStorage.getItem('token');
                            const response = await axios.delete(
                              `${process.env.REACT_APP_BACKEND_URL}/api/delete-account`,
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            
                            alert('Your account has been successfully deleted.');
                            
                            // Log out user
                            logout();
                            navigate('/');
                          } catch (error) {
                            console.error('Error deleting account:', error);
                            alert(error.response?.data?.detail || 'Failed to delete account. Please try again or contact support.');
                          }
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Delete Account
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;